import { record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { z } from 'zod';
import { TooltippedIcon } from '@mews-commander/ui';
import { Prettify } from '@mews/std';
import { IconName } from '@optimus-web/core';
import { FieldProps, getFieldRenderer } from './fields/fields';
import { getFormRenderer, RenderFunction, RenderTemplate } from './layout';
import { FieldNames, FieldNamesObject, SchemaShape, SupportedSchema } from './types';
import { unwrap } from './unwrap';
import { getDefaultRequired } from './utils';

type FieldsDefinition<Schema extends SupportedSchema> = Prettify<{
    [Name in FieldNames<Schema>]: FieldProps<SchemaShape<Schema>[Name], FieldNamesObject<Schema>>;
}>;

export interface SchemaFormDefinition<Schema extends SupportedSchema> {
    fields: FieldsDefinition<Schema>;
    renderFields?: RenderFunction<Schema> | RenderTemplate<Schema>;
}

export const SchemaFormFields = <Schema extends SupportedSchema>({
    schema,
    formDefinition,
    namePrefix,
}: {
    schema: Schema;
    formDefinition: SchemaFormDefinition<Schema>;
    namePrefix?: string;
}) => {
    const unwrappedSchema = unwrap<SupportedSchema>(schema);

    const fieldNames = pipe(
        formDefinition.fields as Record<string, FieldProps>,
        record.mapWithIndex((name) => (namePrefix ? `${namePrefix}.${name}` : name))
    );

    return pipe(
        formDefinition.fields as Record<string, FieldProps>,
        record.mapWithIndex((name, fieldProps) => (
            <SingleField
                key={name}
                name={namePrefix ? `${namePrefix}.${name}` : name}
                schema={unwrappedSchema.shape[name]}
                fieldProps={fieldProps}
                fieldNames={fieldNames}
            />
        )),
        getFormRenderer(formDefinition.renderFields)
    );
};

export const SingleField = <FieldSchema extends z.ZodTypeAny, FieldNames extends Record<string, string>>({
    name,
    schema,
    fieldProps,
    fieldNames = {} as FieldNames,
}: {
    name: string;
    schema: FieldSchema;
    fieldProps: FieldProps<FieldSchema>;
    fieldNames?: FieldNames;
}) => {
    const unwrappedSchema = unwrap(schema);
    const renderer = fieldProps.render ?? getFieldRenderer(fieldProps.field, unwrappedSchema);
    return (
        <>
            {renderer(
                {
                    name,
                    label: fieldProps.label,
                    disabled: fieldProps.disabled,
                    helper: fieldProps.helper,
                    placeholder: fieldProps.placeholder,
                    readOnly: fieldProps.readOnly,
                    required: fieldProps.required ?? getDefaultRequired(schema),
                    captionIconWithTooltip: fieldProps.tooltipHelper ? (
                        <TooltippedIcon size="small" value={IconName.Info} tooltip={fieldProps.tooltipHelper} />
                    ) : undefined,
                },
                {
                    rawSchema: schema,
                    schema: unwrappedSchema,
                    fieldNames,
                }
            )}
        </>
    );
};
